import { motion } from 'framer-motion'
import { PropsWithChildren, ReactNode, forwardRef } from 'react'
import classNames from 'classnames'
import { ClassName } from '@/global/types'
import { useDrawer, useMediaQuery } from '@/global/hooks'
import { Portal } from '@/global/components/Portal'
import { WButton } from '@alice-health/wonderland-react'
import { zIndex } from '@/global/constants/z-index'

type SlidePanelPosition = 'left' | 'right' | 'bottom'

interface SlidePanelProps {
  id: string
  className?: ClassName
  position?: SlidePanelPosition
  /** Lado de onde o painel entra abaixo de `lg`. Sem valor, segue `position`. */
  mobilePosition?: SlidePanelPosition
  backdrop?: boolean
  closeButton?: boolean
  /** Botoes extras, renderizados a esquerda do fechar. */
  actions?: ReactNode
  /**
   * Cartao flutuante com 8px de folga nas quinas, como o painel do menu
   * (`.navbar-drawer-panel`). Abaixo de `lg` o painel volta a ocupar a tela
   * inteira, senao a folga estouraria a largura.
   */
  floating?: boolean
  onClose?: () => void
}

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 0.5 }
}

const panelVariants = {
  left: {
    hidden: { transform: 'translateX(-100%)' },
    visible: { transform: 'translateX(0)' }
  },
  right: {
    hidden: { transform: 'translateX(100%)' },
    visible: { transform: 'translateX(0)' }
  },
  /* A folga de 8px entra na conta, senao sobra uma tira do painel na tela. */
  rightFloating: {
    hidden: { transform: 'translateX(calc(100% + 8px))' },
    visible: { transform: 'translateX(0)' }
  },
  bottom: {
    hidden: { transform: 'translateY(100%)' },
    visible: { transform: 'translateY(0)' }
  }
}

/* `lg` do projeto e 1024px: abaixo disso vale o `mobilePosition`. */
const MOBILE = { maxWidth: 'tablet_landscape' } as const

const SlidePanel = forwardRef<
  HTMLDivElement,
  PropsWithChildren<SlidePanelProps>
>(function SlidePanel(
  {
    id,
    children,
    className,
    position = 'right',
    mobilePosition,
    backdrop = true,
    closeButton = true,
    actions,
    floating = false,
    onClose
  },
  ref
) {
  const { isOpen, close } = useDrawer(id)

  const isMobile = useMediaQuery(MOBILE)

  const activePosition = isMobile && mobilePosition ? mobilePosition : position

  const bottomSheet = position === 'right' && mobilePosition === 'bottom'

  const variant =
    floating && activePosition === 'right' ? 'rightFloating' : activePosition

  const closeSlidePanel = () => {
    close()
    onClose?.()
  }

  return (
    <Portal wrapperId={id}>
      {backdrop ? (
        <motion.div
          onClick={closeSlidePanel}
          initial='hidden'
          animate={isOpen ? 'visible' : 'hidden'}
          variants={backdropVariants}
          transition={{ duration: 0.3 }}
          className={classNames(
            'fixed w-screen h-screen inset-0 bg-gl-color-shades-gray-100',
            zIndex.global.slidePanel,
            !isOpen && 'pointer-events-none'
          )}
        />
      ) : null}

      <motion.div
        data-testid='slide-panel'
        ref={ref}
        /*
         * Valores no lugar de rotulo de variante: o `variant` muda quando a
         * media query resolve, e o framer nao reavalia um rotulo que continua o
         * mesmo. Com objeto, cada render entrega a posicao certa.
         */
        initial={panelVariants[variant].hidden}
        animate={
          isOpen
            ? panelVariants[variant].visible
            : panelVariants[variant].hidden
        }
        transition={{ duration: 0.3 }}
        className={classNames(
          /*
           * A rolagem fica no wrapper interno, e nao aqui. Um popup do design
           * system aberto pelos `actions` e `fixed`, mas o transform da animacao
           * faz dele um filho deste painel: com a rolagem aqui, a sobra do
           * popup viraria barra horizontal.
           */
          'fixed bg-gl-color-shades-white shadow-lg min-w-[300px]',
          zIndex.global.slidePanel,
          {
            'top-0 left-0 h-full': position === 'left',
            'top-0 right-0 h-full': position === 'right' && !bottomSheet,
            'bottom-0 left-0 w-full': position === 'bottom',
            /*
             * Bandeja de 90vh no mobile: a altura menor deixa a pagina aparecer
             * atras e sustenta a entrada pela base. As classes de `lg` abaixo
             * devolvem o painel para a lateral.
             */
            'bottom-0 left-0 w-full h-[90vh] lg:left-auto': bottomSheet,
            /* Sem `floating` a lateral ocupa a altura toda. */
            'lg:top-0 lg:bottom-auto lg:right-0 lg:h-full':
              bottomSheet && !floating
          },
          floating &&
            position === 'right' &&
            'lg:inset-y-gl-spacing-02 lg:right-gl-spacing-02 lg:h-auto',
          className
        )}
      >
        {closeButton || actions ? (
          <div className='absolute top-8 right-8 z-[110] flex items-center gap-gl-spacing-02'>
            {actions}

            {closeButton ? (
              <WButton
                aria-label='Fechar'
                iconButton
                icon='icClose'
                variant='white'
                size='medium'
                onClick={closeSlidePanel}
              />
            ) : null}
          </div>
        ) : null}

        <div className='h-full overflow-y-auto'>{children}</div>
      </motion.div>
    </Portal>
  )
})

export { SlidePanel }
